import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuFilter from '@/components/DropdownMenu/DropdownMenuFilter';
import PropTypes from 'prop-types';
import Tooltip from '@/components/Tooltip';
import { isDarkTheme } from '@/constants/Colors';

// Create styles outside component
const isMacOS = Platform.OS === 'macos';
const SidebarHeader = isMacOS ? Pressable : View;
// Radix supplies the web interaction handlers to its `asChild` trigger. A
// Pressable wraps those props in another stateful style pass, which turns the
// merged style back into an array and sends invalid numeric CSS keys to React.
const SidebarFilterTrigger = Platform.OS === 'web' ? View : Pressable;

const createStyles = (colors, isXS, hoveredLabel) => StyleSheet.create({
  container: {
    backgroundColor: colors.solid_color_block,
    marginTop: 8,
    ...(isMacOS ? { borderTopWidth: 0 } : {}),
  },
  header: {
    minHeight: isMacOS ? 32 : (isXS ? 38 : 28),
    height: isMacOS ? 32 : undefined,
    paddingRight: 8,
    paddingLeft: 10,
    justifyContent: 'center',
    borderRadius: isMacOS ? 6 : 4,
    marginBottom: isMacOS ? 1 : 4,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    textTransform: 'capitalize',
    textDecorationLine: hoveredLabel === 'title' ? 'underline' : 'none',
  },
  labelText: {
    textTransform: 'capitalize',
    textDecorationLine: 'none',
  },
  labelTextHovered: {
    textDecorationLine: 'underline',
  },
  labelSeparator: {
    marginHorizontal: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  filterIcon: {
    marginLeft: 4,
    padding: 4,
    borderRadius: 4,
    transition: 'background-color 0.2s ease',
  },
  collapseIcon: {
    marginLeft: 8,
    padding: 4,
    borderRadius: 4,
  },
  titleButton: {
    padding: 4,
    paddingLeft: 0,
    borderRadius: 4,
  },
  labelButton: {
    padding: 4,
    borderRadius: 4,
  },
});

// Memoized label component
const Label = React.memo(({ 
  label, 
  hoveredLabel, 
  onLabelClick, 
  setHoveredLabel, 
  isXS,
  styles 
}) => (
  <React.Fragment key={label.id}>
    <Text color='tertiary' size={isXS ? 's' : 'xs'} style={styles.labelSeparator}>›</Text>
    <Pressable 
      onPress={() => onLabelClick?.(label.id)}
      style={styles.labelButton}
      onHoverIn={() => setHoveredLabel(label.id)}
      onHoverOut={() => setHoveredLabel(null)}
      data-label-button="true"
      accessibilityRole="button"
      accessibilityLabel={`Filter by ${label.title}`}
    >
      <Text 
        size={isXS ? 's' : 'xs'}
        weight='medium'
        color='tertiary'
        style={[
          styles.labelText,
          hoveredLabel === label.id && styles.labelTextHovered
        ]}
      >
        {label.title}
      </Text>
    </Pressable>
  </React.Fragment>
));

Label.propTypes = {
  label: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  hoveredLabel: PropTypes.string,
  onLabelClick: PropTypes.func.isRequired,
  setHoveredLabel: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
};

Label.displayName = 'Label';

function SidebarListContainer({
  title,
  labels = [],
  currentLabelPath = [],
  isCollapsed,
  onToggle,
  onLabelClick,
  onActionClick,
  stickyIndexTop = 0,
  containerRef,
  headerAction,
  keepActionsVisible = false,
}) {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const filterButtonRef = useRef(null);
  const headerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Use native DOM mouseenter/mouseleave for reliable hover tracking on web.
  // React Native Web's onHoverIn/onHoverOut can lose track of hover state
  // when the pointer crosses into nested non-RNW elements (Radix buttons,
  // Tooltip spans, etc.) because those elements aren't part of the RNW
  // responder system.
  useEffect(() => {
    if (Platform.OS !== 'web' || isXS) return;
    const el = headerRef.current;
    if (!el) return;
    const onEnter = () => setIsHovered(true);
    const onLeave = () => setIsHovered(false);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [isXS]);

  const rowHoverBg = useMemo(
    () => (isDarkTheme(theme) ? 'rgba(255, 255, 255, 0.12)' : colors.solid_color_secondary),
    [theme, colors.solid_color_secondary],
  );

  // Memoize styles
  const styles = useMemo(
    () => createStyles(colors, isXS, hoveredLabel),
    [colors, isXS, hoveredLabel]
  );

  // Container style
  const containerStyle = useMemo(() => ({
    ...styles.container,
  }), [styles.container]);

  // Memoize header actions opacity
  const headerActionsStyle = useMemo(() => ({
    ...styles.headerActions,
    opacity: (isXS || isMacOS || keepActionsVisible || isFocused) ? 1 : (isHovered || isDropdownOpen ? 1 : 0),
  }), [styles.headerActions, isXS, isHovered, isDropdownOpen, keepActionsVisible, isFocused]);

  // Memoized handlers
  const handleHeaderClick = useCallback((e) => {
    const target = e?.target;
    const isFilterClick = filterButtonRef.current?.contains?.(target);
    const isLabelClick = target?.closest?.('[data-label-button="true"]');
    const isDropdownClick = target?.closest?.('[role="menu"]') ||
                          target?.closest?.('[role="menuitem"]') ||
                          target?.closest?.('[role="menuitemcheckbox"]') ||
                          target?.closest?.('[role="menuitemradio"]') ||
                          target?.closest?.('[role="separator"]') ||
                          target?.closest?.('[role="group"]');
    
    if (!isFilterClick && !isLabelClick && !isDropdownClick) {
      onToggle();
    }
  }, [onToggle]);

  const handleDropdownOpenChange = useCallback((open) => {
    setIsDropdownOpen(open);
  }, []);

  return (
    <View style={containerStyle} ref={containerRef}>
      <SidebarHeader
        ref={headerRef}
        onHoverIn={isMacOS ? () => setIsHovered(true) : undefined}
        onHoverOut={isMacOS ? () => setIsHovered(false) : undefined}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={[
          styles.header,
          isHovered && { backgroundColor: rowHoverBg },
          Platform.OS === 'web' && { cursor: 'pointer' },
        ]}
        // Web already has explicit, keyboard-accessible buttons for the
        // title, filters, and collapse action. Giving their shared wrapper a
        // button role creates invalid nested <button> markup and hydration
        // errors on every Work thread.
        accessibilityRole={Platform.OS === 'web' ? undefined : 'button'}
        accessibilityLabel={Platform.OS === 'web'
          ? undefined
          : `${title} section, ${isCollapsed ? 'collapsed' : 'expanded'}`}
        onClick={Platform.OS === 'web' ? handleHeaderClick : undefined}
        onPress={Platform.OS !== 'web' ? handleHeaderClick : undefined}
      >
        <View style={styles.headerContent}>
          <View style={styles.labelContainer}>
            <Pressable 
              onPress={() => onLabelClick?.(null)}
              style={styles.titleButton}
              onHoverIn={() => setHoveredLabel('title')}
              onHoverOut={() => setHoveredLabel(null)}
              data-label-button="true"
              accessibilityRole="button"
              accessibilityLabel={`Reset ${title} filters`}
            >
              <Text color='tertiary' size='s' weight='bold' style={styles.headerTitle}>{title}</Text>
            </Pressable>

            {currentLabelPath.map((label) => (
              <Label
                key={label.id}
                label={label}
                hoveredLabel={hoveredLabel}
                onLabelClick={onLabelClick}
                setHoveredLabel={setHoveredLabel}
                styles={styles}
              />
            ))}
          </View>

          <View style={headerActionsStyle}>
            {headerAction && <View
              style={{ opacity: isMacOS && !isHovered && !isFocused && !keepActionsVisible ? 0 : 1 }}
            >{headerAction}</View>}
            {labels.length > 0 && (
              <DropdownMenu.Root onOpenChange={handleDropdownOpenChange}>
                <Tooltip label="Open filters menu">
                  <DropdownMenu.Trigger asChild>
                    <SidebarFilterTrigger
                      ref={filterButtonRef}
                      // Radix's `asChild` merges this style object into its
                      // trigger props. Passing an RN style array gives the DOM
                      // numeric CSS properties ("0", "1") and crashes React.
                      style={StyleSheet.flatten([
                        styles.filterIcon,
                        isDropdownOpen && { backgroundColor: rowHoverBg }
                      ])}
                      accessibilityRole="button"
                      accessibilityLabel="Open filters menu"
                    >
                      <Icon
                        name="filter-list"
                        size={isXS ? 18 : 14}
                        color={colors.text_color_secondary}
                      />
                    </SidebarFilterTrigger>
                  </DropdownMenu.Trigger>
                </Tooltip>
                <DropdownMenuFilter
                  labels={labels}
                  currentLabelPath={currentLabelPath}
                  onLabelClick={onLabelClick}
                  onActionClick={onActionClick}
                />
              </DropdownMenu.Root>
            )}

            <Tooltip label={isCollapsed ? 'Expand' : 'Collapse'}>
              <Pressable 
                onPress={onToggle}
                style={styles.collapseIcon}
                accessibilityRole="button"
                accessibilityLabel={`${isCollapsed ? 'Expand' : 'Collapse'} section`}
              >
                <Icon
                  name={isCollapsed ? 'chevron-right' : 'chevron-down'}
                  size={isXS ? 18 : 14}
                  color={colors.text_color_secondary}
                />
              </Pressable>
            </Tooltip>
          </View>
        </View>
      </SidebarHeader>
    </View>
  );
}

SidebarListContainer.propTypes = {
  title: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.array,
  })),
  currentLabelPath: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })),
  isCollapsed: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onLabelClick: PropTypes.func.isRequired,
  onActionClick: PropTypes.func,
  stickyIndexTop: PropTypes.number,
};

SidebarListContainer.displayName = 'SidebarListContainer';

export default React.memo(SidebarListContainer);
